import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Gift, TrendingDown, Search, Loader2, ShoppingCart, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${Math.round(n || 0).toLocaleString('es-AR')}`;

const PriceListPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const r = await axios.get(`${API}/price-list/tiered`, { withCredentials: true, params: { _t: Date.now() } });
        setData(r.data);
      } catch (e) {
        setError('No pudimos cargar la lista de precios. Probá de nuevo en un momento.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredGroups = useMemo(() => {
    if (!data?.groups) return [];
    const q = query.trim().toLowerCase();
    return data.groups
      .filter(g => activeGroup === 'all' || g.name === activeGroup)
      .map(g => ({
        ...g,
        products: g.products.filter(p => !q || (p.name || '').toLowerCase().includes(q))
      }))
      .filter(g => g.products.length > 0);
  }, [data, query, activeGroup]);

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      // Descargar como blob con el header Authorization presente (interceptor
      // axios) — window.open no lleva Bearer en sesiones sin cookie y termina
      // en un JSON de 403.
      const resp = await axios.get(`${API}/price-list/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lista-precios-${new Date().toISOString().slice(0,10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
      setError('No pudimos descargar el PDF. Probá de nuevo en un momento.');
    } finally {
      setExporting(false);
    }
  };

  const goBack = () => navigate(user ? '/account' : '/products');

  // "Hacer Pedido" button: si el usuario está logueado → catálogo directo.
  // Si no, sigue a /products donde AppGate/WelcomeGate abre el flujo de
  // registro/login (Google) aprovechando que ya vieron los precios.
  const handleHacerPedido = () => navigate('/products');

  // 📱 Hint de scroll horizontal para mobile: animamos un shadow inset por
  // ~4s la primera vez que se abre la página para insinuar que hay más
  // columnas (tiers de descuento) al deslizar hacia la derecha.
  const tableScrollRef = useRef(null);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  useEffect(() => {
    if (!showSwipeHint) return;
    if (loading) return; // esperar a que la tabla esté montada
    const el = tableScrollRef.current;
    const onScroll = () => setShowSwipeHint(false);
    if (el) el.addEventListener('scroll', onScroll, { once: true, passive: true });
    const t = setTimeout(() => setShowSwipeHint(false), 4200);
    return () => {
      if (el) el.removeEventListener('scroll', onScroll);
      clearTimeout(t);
    };
  }, [showSwipeHint, loading]);

  // 🧷 Header inteligente: se oculta con scroll hacia abajo y reaparece apenas
  // se sube (mismo patrón que el catálogo en ProductListPage.js). Agrupa el
  // buscador y el botón "Hacer Pedido" para que estén siempre accesibles.
  const [headerVisible, setHeaderVisible] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerWrapperRef = useRef(null);
  const lastScrollYRef = useRef(0);

  useEffect(() => {
    let raf = null;
    const compute = () => {
      raf = null;
      const y = window.scrollY || window.pageYOffset || 0;
      const prev = lastScrollYRef.current;
      const delta = y - prev;
      if (y < 80) {
        setHeaderVisible(true);
        lastScrollYRef.current = y;
        return;
      }
      if (delta > 12) {
        setHeaderVisible(false);
        lastScrollYRef.current = y;
      } else if (delta < -12) {
        setHeaderVisible(true);
        lastScrollYRef.current = y;
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(compute);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => {
    const el = headerWrapperRef.current;
    if (!el) return;
    const update = () => setHeaderHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <button onClick={goBack} className="flex items-center gap-2 text-slate-600 mb-6" data-testid="price-list-back-btn">
          <ArrowLeft size={20} /> Volver
        </button>
        <div className="max-w-md mx-auto bg-white rounded-2xl p-8 text-center shadow-sm border border-slate-200">
          <p className="text-slate-700 font-medium" data-testid="price-list-error">{error}</p>
        </div>
      </div>
    );
  }

  const tiers = data?.tiers || [];
  const featured = data?.featuredGift;
  const groups = data?.groups || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="price-list-page">
      {/* Header inteligente: fixed + se oculta al bajar, reaparece al subir */}
      <div
        ref={headerWrapperRef}
        className={`fixed left-0 right-0 top-0 z-20 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm transition-transform duration-300 ease-out ${headerVisible ? 'translate-y-0' : '-translate-y-full'}`}
        data-testid="price-list-sticky-header"
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={goBack} className="p-2 -ml-2 rounded-lg hover:bg-slate-100" data-testid="price-list-back-btn">
            <ArrowLeft size={22} className="text-slate-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-slate-900 truncate" style={{ fontFamily: 'Barlow Condensed' }}>Lista de Precios</h1>
            <p className="text-[11px] text-slate-500">{data?.storeName || 'Bebida Bacana'} · Actualizada al {new Date().toLocaleDateString('es-AR')}</p>
          </div>
          <button
            onClick={handleHacerPedido}
            className="group relative hidden sm:flex items-center gap-1.5 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-600 text-white px-4 py-2 rounded-lg text-sm font-black uppercase tracking-wide transition-all shadow-lg shadow-emerald-500/40 hover:shadow-emerald-500/60 hover:-translate-y-0.5 active:scale-95 overflow-hidden"
            style={{ animation: 'pricingCtaPulse 2.4s ease-in-out infinite' }}
            data-testid="price-list-hacer-pedido-btn"
          >
            <span
              className="absolute top-0 bottom-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent pointer-events-none"
              style={{ animation: 'pricingCtaShine 2.6s linear infinite' }}
              aria-hidden
            />
            <ShoppingCart size={16} className="relative z-10" />
            <span className="relative z-10">Hacer Pedido</span>
            <ChevronRight size={14} className="relative z-10 -mr-0.5 opacity-90 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button
            onClick={handleExportPdf}
            disabled={exporting}
            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
            data-testid="price-list-export-pdf-btn"
          >
            {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />}
            <span className="hidden sm:inline">Exportar PDF</span>
          </button>
        </div>

        {/* CTA Mobile — botón grande visible sólo en pantallas chicas, ahora
            dentro del header que se oculta/reaparece con el scroll. */}
        <div className="sm:hidden max-w-7xl mx-auto px-4 pb-2">
          <button
            onClick={handleHacerPedido}
            className="group relative w-full flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-600 text-white px-4 py-3 rounded-xl text-sm font-black uppercase tracking-wide transition-all shadow-lg shadow-emerald-500/40 hover:shadow-emerald-500/60 active:scale-[0.98] overflow-hidden"
            style={{ animation: 'pricingCtaPulse 2.4s ease-in-out infinite' }}
            data-testid="price-list-hacer-pedido-mobile-btn"
          >
            <span
              className="absolute top-0 bottom-0 w-1/4 bg-gradient-to-r from-transparent via-white/50 to-transparent pointer-events-none"
              style={{ animation: 'pricingCtaShine 2.6s linear infinite' }}
              aria-hidden
            />
            <ShoppingCart size={18} className="relative z-10 group-active:scale-90 transition-transform" />
            <span className="relative z-10">Hacer Pedido</span>
            <ChevronRight size={16} className="relative z-10 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        {/* Buscador + filtro de grupo — ahora parte del header que se
            oculta/reaparece con el scroll (antes desaparecía al bajar). */}
        <div className="max-w-7xl mx-auto px-4 pb-3 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar producto..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              data-testid="price-list-search"
            />
          </div>
          <select
            value={activeGroup}
            onChange={(e) => setActiveGroup(e.target.value)}
            className="px-3 py-2.5 border border-slate-300 rounded-lg text-sm bg-white"
            data-testid="price-list-group-filter"
          >
            <option value="all">Todos los grupos</option>
            {groups.map(g => (
              <option key={g.name} value={g.name}>{g.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 space-y-4" style={{ paddingTop: `${headerHeight + 16}px` }}>
        {/* 🎁 Gift headline banner */}
        {featured && featured.product && (
          <div className="rounded-2xl border-2 border-orange-300 bg-gradient-to-r from-amber-50 via-orange-50 to-pink-50 p-4 flex items-center gap-4 shadow-sm" data-testid="price-list-gift-banner">
            <div className="relative flex-shrink-0">
              <img
                src={featured.product.imageUrl}
                alt={featured.product.name}
                className="w-16 h-16 sm:w-20 sm:h-20 object-contain bg-white rounded-xl border-2 border-orange-400 shadow-md"
                data-testid="price-list-gift-image"
              />
              <span className="absolute -top-2 -right-2 text-2xl">🎁</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase font-black tracking-widest text-orange-600 mb-0.5">Regalo por objetivo</p>
              <p className="text-sm sm:text-base font-bold text-slate-900 leading-snug" data-testid="price-list-gift-name">{featured.product.name}</p>
              <p className="text-xs sm:text-sm text-orange-700 mt-1">
                ¡Llevátelo <span className="font-extrabold">GRATIS</span> con tu compra superior a{' '}
                <span className="font-extrabold text-orange-800" data-testid="price-list-gift-threshold">{fmt(featured.threshold)}</span>!
              </p>
            </div>
          </div>
        )}

        {/* Winner-takes-all note */}
        {tiers.length > 0 && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-start gap-2 text-[12px] text-emerald-800" data-testid="price-list-winner-note">
            <TrendingDown size={16} className="flex-shrink-0 mt-0.5" />
            <p>
              El precio de cada columna aplica el <strong>mayor descuento disponible</strong> entre el descuento por objetivo de carrito y las ofertas por cantidad del producto.
              {data?.permanentDiscountPct > 0 && <> Además ya incluye tu descuento permanente de <strong>{data.permanentDiscountPct}%</strong>.</>}
            </p>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden relative">
          {/* Hint de scroll horizontal en mobile: fade + shadow pulsante en el
              borde derecho que insinúa "hay más columnas al deslizar". */}
          {showSwipeHint && (
            <div
              className="pointer-events-none absolute top-0 right-0 h-full w-24 sm:hidden z-10"
              style={{ animation: 'priceListSwipeHint 1600ms ease-in-out infinite' }}
              data-testid="price-list-swipe-hint"
            >
              <div className="absolute top-1/2 right-2 -translate-y-1/2 flex items-center gap-1 bg-slate-900/85 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow">
                <ChevronRight size={12} /> Deslizá
              </div>
            </div>
          )}
          <div className="overflow-x-auto" ref={tableScrollRef}>
            <table className="w-full text-sm border-separate border-spacing-0" data-testid="price-list-table">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider whitespace-nowrap sticky left-0 bg-slate-900 z-20 shadow-[2px_0_0_0_rgba(255,255,255,0.05)]">Producto</th>
                  <th className="text-right px-3 py-3 font-bold text-xs uppercase tracking-wider whitespace-nowrap">Precio base</th>
                  {tiers.map(t => (
                    <th
                      key={t.threshold}
                      className="text-center px-3 py-3 font-bold text-xs uppercase whitespace-nowrap border-l border-slate-700"
                      data-testid={`price-list-tier-header-${Math.round(t.threshold)}`}
                    >
                      <div className="flex flex-col items-center gap-0.5">
                        <span className="opacity-80 text-[10px] font-medium tracking-wider">Precio por compra</span>
                        <span className="text-sm font-black">&gt; {fmt(t.threshold)}</span>
                        {t.giftProduct && (
                          <span className="text-[9px] font-bold text-orange-300 flex items-center gap-0.5">
                            <Gift size={10} /> con regalo
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              {filteredGroups.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={2 + tiers.length} className="p-8 text-center text-slate-400" data-testid="price-list-empty">
                      No hay productos que coincidan con la búsqueda.
                    </td>
                  </tr>
                </tbody>
              )}
              {filteredGroups.map((g) => (
                <tbody key={g.name} data-testid={`price-list-group-${g.name}`}>
                  <tr>
                    <td colSpan={2 + tiers.length} className="bg-slate-100 p-0 border-y border-slate-200">
                      {/* Wrapper sticky para que el nombre del grupo NO se
                          pierda al scrollear horizontalmente en móvil (el
                          <td> con colSpan+sticky se desliza). */}
                      <div className="sticky left-0 px-4 py-2 font-bold text-slate-800 text-xs uppercase tracking-wider inline-block">
                        {g.name}
                      </div>
                    </td>
                  </tr>
                  {g.products.map((p, idx) => {
                    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50';
                    const minQty = Number(p.minQuantity || 0);
                    return (
                    <tr
                      key={p.id}
                      className={`${rowBg} hover:bg-amber-50/60`}
                      data-testid={`price-list-row-${p.id}`}
                    >
                      <td className={`px-4 py-2.5 font-medium text-slate-800 max-w-[280px] sticky left-0 ${rowBg} z-10 shadow-[2px_0_0_0_rgba(15,23,42,0.06)]`}>
                        <div className="flex items-center gap-2">
                          {p.imageUrl && (
                            <img
                              src={p.imageUrl}
                              alt=""
                              className="w-9 h-9 object-contain rounded bg-white border border-slate-200 flex-shrink-0"
                              loading="lazy"
                            />
                          )}
                          <div className="min-w-0">
                            <span className="line-clamp-2 leading-tight block" translate="no">{p.name}</span>
                            {minQty > 1 && (
                              <span
                                className="inline-block mt-0.5 text-[10px] font-semibold text-slate-500"
                                data-testid={`price-list-min-${p.id}`}
                              >
                                Mínimo: {minQty} un.
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-semibold text-slate-900 whitespace-nowrap" data-testid={`price-list-base-${p.id}`}>
                        {fmt(p.basePrice)}
                      </td>
                      {tiers.map(t => {
                        const cell = p.byTier?.[String(Math.round(t.threshold))];
                        const finalPrice = cell?.finalPrice || 0;
                        const winnerPct = cell?.winnerPct || 0;
                        const hasDiscount = finalPrice > 0 && finalPrice < p.basePrice;
                        return (
                          <td
                            key={t.threshold}
                            className="px-3 py-2.5 text-center whitespace-nowrap border-l border-slate-100"
                            data-testid={`price-list-cell-${p.id}-${Math.round(t.threshold)}`}
                          >
                            {hasDiscount ? (
                              <div className="flex flex-col items-center leading-tight">
                                <span className="font-black text-emerald-700">{fmt(finalPrice)}</span>
                                <span className="text-[10px] font-bold text-emerald-600 mt-0.5">-{Math.round(winnerPct)}%</span>
                              </div>
                            ) : cell?.excluded ? (
                              <div className="flex flex-col items-center leading-tight" title="Este producto no participa del descuento por objetivo">
                                <span className="font-semibold text-slate-700">{fmt(p.basePrice)}</span>
                                <span className="text-[9px] font-medium text-amber-600 mt-0.5">sin descuento</span>
                              </div>
                            ) : (
                              <span className="font-semibold text-slate-700">{fmt(p.basePrice)}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 py-4">
          Precios sujetos a cambio sin previo aviso · Generado {new Date().toLocaleString('es-AR')}
        </p>
      </div>
    </div>
  );
};

export default PriceListPage;
